import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, fromEvent, map, merge, takeUntil } from 'rxjs';

export interface HorizontalUserScrollEvent {
  scrollLeft: number;
  scrollPercentage: number;
  atEnd: boolean;
}

/**
 * Generic, reusable directive that emits horizontal scroll events only when
 * they are caused by direct user interaction (drag, swipe, wheel) and ignores
 * programmatic scrolls such as snap-back animations performed by libraries
 * like ngx-drag-scroll.
 *
 * Why this is non-trivial:
 * - Libraries (e.g. ngx-drag-scroll) animate the scroll position after the
 *   user finishes a gesture to snap to the nearest item. Those programmatic
 *   scrolls fire real DOM `scroll` events which would otherwise be confused
 *   with user-initiated scrolls.
 *
 * Strategy:
 * 1. Resolve the actual scrollable element. By default we look up
 *    `scrollableSelector` (default `.drag-scroll-content`, used by
 *    ngx-drag-scroll) and fall back to the host element.
 * 2. Maintain an `isUserInteracting` flag that is true only while the user
 *    is actively interacting (pointer down, touch active, or wheel spinning).
 * 3. On interaction end, keep the flag true for a short grace period
 *    (`interactionGraceMs`) so we still capture inertia / native momentum
 *    scroll, but ignore programmatic snap animations that run afterwards.
 * 4. Debounce the resulting stream once and use it to feed both outputs:
 *    - `userScrolled` always fires after a user-driven scroll settles.
 *    - `nearEndReached` fires only when `scrollPercentage > nearEndThreshold`.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appHorizontalUserScroll]',
})
export class HorizontalUserScrollDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** Percentage of horizontal scroll range that triggers `nearEndReached`. */
  @Input() nearEndThreshold = 80;
  /** Debounce window applied to outputs. */
  @Input() debounceMs = 200;
  /**
   * Grace window after pointer/touch/wheel ends before scroll events are
   * ignored. Allows momentum / inertia scroll to still count as user-driven.
   */
  @Input() interactionGraceMs = 150;
  /**
   * CSS selector for an internal scrollable child element (e.g. ngx-drag-scroll's
   * `.drag-scroll-content`). When null/empty or the child is not found we fall
   * back to the host element.
   */
  @Input() scrollableSelector: string | null = '.drag-scroll-content';
  /** When true, the directive does not wire up any listeners. */
  @Input() disabled = false;

  /** Emits after a user-driven scroll settles (debounced). */
  @Output() userScrolled = new EventEmitter<HorizontalUserScrollEvent>();
  /** Emits when a user-driven scroll crosses `nearEndThreshold` (debounced). */
  @Output() nearEndReached = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();
  private readonly scrolledTrigger$ = new Subject<HorizontalUserScrollEvent>();
  private initialized = false;
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  ngAfterViewInit(): void {
    this.initialized = true;
    if (this.disabled) return;
    this.setupListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Allow toggling `disabled` after init: tear down and re-wire as needed.
    if (this.initialized && changes['disabled'] && !changes['disabled'].firstChange) {
      this.destroy$.next();
      if (!this.disabled) {
        this.setupListeners();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.scrolledTrigger$.complete();
  }

  private setupListeners(): void {
    const host = this.hostRef?.nativeElement;
    if (!host) return;

    // Resolve the actual scrollable element. Fall back to the host element.
    const selector = this.scrollableSelector;
    const scrollContent = selector ? host.querySelector<HTMLElement>(selector) : null;
    const target: HTMLElement = scrollContent ?? host;

    // Gates scroll emissions so that snap-back / programmatic scrolls
    // (which fire when the user is no longer touching/dragging) are ignored.
    let isUserInteracting = false;
    let interactionEndTimer: ReturnType<typeof setTimeout> | null = null;

    // Called when the user starts interacting (pointer/touch/wheel).
    // Cancels any pending "interaction end" timer in case the user resumes.
    const markInteractionStart = () => {
      isUserInteracting = true;
      if (interactionEndTimer) {
        clearTimeout(interactionEndTimer);
        interactionEndTimer = null;
      }
    };

    // Called when the user stops interacting. We don't flip the flag
    // immediately because there can still be a brief tail of legitimate
    // scroll events (inertia / momentum). After `interactionGraceMs` anything
    // else is assumed to be the library's snap-back and is ignored.
    const scheduleInteractionEnd = () => {
      if (interactionEndTimer) clearTimeout(interactionEndTimer);
      interactionEndTimer = setTimeout(() => {
        isUserInteracting = false;
        interactionEndTimer = null;
      }, this.interactionGraceMs);
    };

    // Debounced consumer of scroll events. Drives both outputs.
    this.scrolledTrigger$.pipe(debounceTime(this.debounceMs), takeUntil(this.destroy$)).subscribe((event) => {
      this.userScrolled.emit(event);
      if (event.atEnd) {
        this.nearEndReached.emit();
      }
    });

    // Interaction-start sources:
    // - pointerdown: mouse / pen / unified touch (modern browsers)
    // - touchstart: explicit support for older mobile browsers
    // - wheel: mouse wheel / trackpad scroll
    merge(
      fromEvent(target, 'pointerdown'),
      fromEvent(target, 'touchstart', { passive: true }),
      fromEvent(target, 'wheel', { passive: true }),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => markInteractionStart());

    // Interaction-end sources:
    // - pointerup / pointercancel on target: normal release inside the container
    // - pointerup on globalThis: handle release outside the container after a
    //   drag started inside it
    // - touchend / touchcancel: mobile equivalents
    // - wheel: also acts as an end signal; each wheel tick re-arms the timer
    merge(
      fromEvent(target, 'pointerup'),
      fromEvent(target, 'pointercancel'),
      fromEvent(globalThis, 'pointerup'),
      fromEvent(target, 'touchend', { passive: true }),
      fromEvent(target, 'touchcancel', { passive: true }),
      fromEvent(target, 'wheel', { passive: true }),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => scheduleInteractionEnd());

    // Native horizontal scroll listener.
    // `capture: true` is used because scroll events do not bubble.
    // `distinctUntilChanged` on `scrollLeft` filters out scroll events
    // that don't actually change the scroll position.
    fromEvent(target, 'scroll', { capture: true })
      .pipe(
        map(() => target.scrollLeft),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // Ignore programmatic / snap-back scrolls.
        if (!isUserInteracting) return;

        const range = target.scrollWidth - target.clientWidth;
        const scrollPercentage = range > 0 ? (target.scrollLeft / range) * 100 : 0;
        this.scrolledTrigger$.next({
          scrollLeft: target.scrollLeft,
          scrollPercentage,
          atEnd: scrollPercentage > this.nearEndThreshold,
        });
      });
  }
}
